import { HttpClient } from "@angular/common/http";
import { AssociatedPhrase } from "@shared/models/associated-phrase.model";
import { AssocPhrasesRequest } from "@shared/models/assoc-phrases-request";
import { BehaviorSubject, combineLatest, Observable, Subject, throwError } from "rxjs";
import { Config } from "src/app/config";
import { AuthService } from "./auth.service";
import { catchError, map, switchMap } from "rxjs/operators";
import { AssociatedImage, AssociatedTextPhrase, DataElement, ProcessRunStatusResult, ReferencePhrase, ServiceCallInfo } from "@shared/models/common";
import { ProcessStatusPollingService } from "./process-status-polling.service";
import { ReferenceOptionsRequest } from "@shared/models/reference-options-request.model";
import { ReferenceOption } from "@shared/models/reference-option.model";
import { AddedReferencePhrasesPayload } from "@shared/models/added-reference-phrases-payload";
import { ClaimToolSaveChangesModel } from "@shared/models/claim-tool-save-changes.model";
import { ClaimToolElement } from "@shared/models/claim-tool-element.model";
import { CommonService } from "./common.service";
import { Injectable, OnDestroy } from "@angular/core";
import { DocPhraseMergeAndReorderRequest } from "@shared/models/doc-phrase-merge-and-reorder.request";
import { DocPhraseMergeAndReorder } from "@shared/models/doc-phrase-merge-and-reorder.model";
import { ClaimToolElementSaveChangesData } from "@shared/models/claim-tool-element-save-changes-data.model";
import { GenaiModificationResponse } from "@shared/models/gen-ai-modification.model";
import { ClaimToolAssociatedImagePayload } from "@shared/models/claim-tool-associated-image";
import { ClaimToolAssociatedTextPayload } from "@shared/models/claim-tool-associated-phrase";


@Injectable()
export class ClaimToolService implements OnDestroy {

    scaiServiceUrl: string;

    constructor(
         private http: HttpClient,
         private authService: AuthService,
         private commonService: CommonService,
         private processStatusPollingService: ProcessStatusPollingService
    ) {

        this.scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];
    }
    manageContextChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    manageContentChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    manageAssocImagesChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    phraseOrderChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    contentTypeChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    mergedPhrasesChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    splitWordsChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false); 
    phraseTypeCodeChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false); 

    enableSaveChanges$ = combineLatest([
    this.manageContextChanged$,
    this.manageAssocImagesChanged$,
    this.manageContentChanged$,
    this.phraseOrderChanged$,
    this.contentTypeChanged$,
    this.mergedPhrasesChanged$,
    this.splitWordsChanged$,
    this.phraseTypeCodeChanged$
]).pipe(
  map(([a, b, c, d, e, f, g, h]) => {

    return a || b || c || d || e || f || g || h;  
  })
  );


  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

    destroy$ = new Subject();

    getContentTypeOptions(): Observable<DataElement[]> {

      return this.commonService.getContentTypeList();
    }

    getAssocPhrases(refOptions: AssocPhrasesRequest): Observable<AssociatedPhrase[]> {


        const url = this.scaiServiceUrl + `/phrase/assoc-text-options?doc_id=${refOptions.doc_id}&phrase_id=${refOptions.phrase_id}&is_precheck=${refOptions.is_precheck}`;

       return this.http
            .get<AssociatedPhrase[]>(url, {
                headers: this.authService.getRequestHeaders(),
                withCredentials: false,
                responseType: 'json'
            }).pipe(
            map((associatedPhrases: AssociatedPhrase[]) => {

                
                return associatedPhrases;
            }),
            catchError((err) => {
                     
                return this.handleError(err, url)
             }))

    }

    generateModifications(docId: number,phraseId: number,isPrecheck: boolean): Observable<GenaiModificationResponse> {
          const url = `${this.scaiServiceUrl}/phrase/genai-modifications?doc_id=${docId}&is_precheck=${isPrecheck}&phrase_id=${phraseId}`;

          return this.http
            .get<GenaiModificationResponse>(url, {
              headers: this.authService.getRequestHeaders(),
              withCredentials: false,
              responseType: 'json'
            })
            .pipe(
              map((response: GenaiModificationResponse) => response),
              catchError((err) => this.handleError(err, url))
            );
    }


    /** Creates the alg run head, POSTs the claim-tool changes and emits the run number. */
    submitChanges(claimToolElement: ClaimToolElement, claimToolSaveChangesDataInitialValues: ClaimToolElementSaveChangesData): Observable<number> {

        let saveChangesPayload = this.createSaveChangesPayload(claimToolElement, claimToolSaveChangesDataInitialValues);
        if (saveChangesPayload == null) {
            return throwError('Empty payload');
        }
        saveChangesPayload.is_precheck = claimToolElement.isPrecheck;
        saveChangesPayload.phrase_id = claimToolElement.phraseId;
        saveChangesPayload.doc_id = claimToolElement.docId;

        let algRunHeadPayload = {
        doc_id: claimToolElement.docId,
        run_type: claimToolElement.runType,
        env_id: claimToolElement.envId,
        cat_version_id: claimToolElement.catVersionId
        }
        return this.commonService.addAlgRunHead(algRunHeadPayload).pipe(
            switchMap((runNum: number) => {

                saveChangesPayload.run_num = runNum;

                const url = this.scaiServiceUrl + `phrase`;
                return this.http.post(url, saveChangesPayload, {
                    headers: this.authService.getRequestHeaders(),
                    withCredentials: false,
                    responseType: 'json'
                }).pipe(map(() => runNum));
            }));
    }

    saveChanges(claimToolElement: ClaimToolElement, claimToolSaveChangesDataInitialValues: ClaimToolElementSaveChangesData) : Observable<ProcessRunStatusResult> {

        return this.submitChanges(claimToolElement, claimToolSaveChangesDataInitialValues).pipe(
            switchMap((runNum: number) => this.processStatusPollingService.pollUntilDone(runNum, { until$: this.destroy$ }))
        );
    }
    getRefOptions(refOptions: ReferenceOptionsRequest): Observable<ReferenceOption[]> {


        const url = this.scaiServiceUrl + `/phrase/ref-options?doc_id=${refOptions.doc_id}&phrase_id=${refOptions.phrase_id}&is_precheck=${refOptions.is_precheck}`;

       return this.http
            .get<ReferenceOption[]>(url, {
                headers: this.authService.getRequestHeaders(),
                withCredentials: false,
                responseType: 'json'
            }).pipe(
            map((referenceOptions: ReferenceOption[]) => {

                
                return referenceOptions;
            }),
            catchError((err) => {
                     
                return this.handleError(err, url)
             }))

    }
  getOrderedPhrases(docPhraseMergeAndReorderRequest: DocPhraseMergeAndReorderRequest): Observable<DocPhraseMergeAndReorder[]> {
      
    let url = this.scaiServiceUrl + `/page-phrases?doc_id=${docPhraseMergeAndReorderRequest.doc_id}&page_num=${docPhraseMergeAndReorderRequest.page_num}&is_precheck=${docPhraseMergeAndReorderRequest.is_precheck}`;
    

       return this.http
            .get<DocPhraseMergeAndReorder[]>(url, {
                headers: this.authService.getRequestHeaders(),
                withCredentials: false,
                responseType: 'json'
            }).pipe(
            map((phrases: DocPhraseMergeAndReorder[]) => {

                
                return phrases.sort((a, b) => a.page_order_id - b.page_order_id);
            }),
            catchError((err) => {
                     
                return this.handleError(err, url)
             }))
  }

  private handleError(error: any, url: string): Observable<never> {
    const info = new ServiceCallInfo(url, url, null);
    error.message = error.error.description || error.statusText;

    return this.authService.handleError(error, info);
  }
  private filterAssocTextBasedOnInitial(arrToFilter: AssociatedTextPhrase[], initialArr: AssociatedTextPhrase[], shouldExist=true): ClaimToolAssociatedTextPayload[] {
    let filteredArray: ClaimToolAssociatedTextPayload[] = [];
    for (var i=0; i<arrToFilter.length; i++) {
      var curPhrase = arrToFilter[i];
      const exist = initialArr.findIndex(initialRef => initialRef.assocPhraseId == curPhrase.assocPhraseId);
      if ((exist != -1 && shouldExist) || (exist == -1 && !shouldExist)) {

        filteredArray.push({
          text_id:  curPhrase.assocPhraseId,
          de_assoc_others: curPhrase.deAssocOthers
        });

      }
    }    
    return filteredArray;
  }

  private filterAssocImagesBasedOnInitial(arrToFilter: ClaimToolAssociatedImagePayload[], initialArr: ClaimToolAssociatedImagePayload[], shouldExist=true): ClaimToolAssociatedImagePayload[] {
    let filteredArray: ClaimToolAssociatedImagePayload[] = [];
    for (var i = 0; i < arrToFilter.length; i++) {
      var curImage = arrToFilter[i];
      const exist = initialArr.findIndex(initialImg => initialImg.image_id == curImage.image_id);
      if ((exist != -1 && shouldExist) || (exist == -1 && !shouldExist)) {
        filteredArray.push(curImage);
      }
    }
    return filteredArray;
  }

  isDisabledSaveChangesButton(claimToolElement: ClaimToolElement, claimToolSaveChangesDataInitialValues: ClaimToolSaveChangesModel) {
    
    const claimToolElementSaveChangesStr = JSON.stringify(claimToolElement.claimToolElementSaveChangesData);
    const claimToolSaveChangesInitialValuesStr = JSON.stringify(claimToolSaveChangesDataInitialValues);
    return  claimToolElementSaveChangesStr == claimToolSaveChangesInitialValuesStr;
  } 

  private filterReferencesBasedOnInitial(arrToFilter: ReferencePhrase[], initialArr: ReferencePhrase[]) {
    let filteredArray: AddedReferencePhrasesPayload[] = [];
    for(var i=0; i<arrToFilter.length; i++) {
      var curReference = arrToFilter[i];
      // for deleting only check id
      // for add check id and phrase word id
      const exist = initialArr.findIndex(initialRef => initialRef.refPhraseId == curReference.refPhraseId);
      if (exist == -1) {
        
        filteredArray.push({
          ref_phrase_id: curReference.refPhraseId,
          pointer_word_id: curReference.pointerWordId
        });
      } 
    }
    return filteredArray;
  }
  private filterDeletedReferencesBasedOnInitial(arrToFilter: ReferencePhrase[], initialArr: ReferencePhrase[]) {
    let filteredArray: number[] = [];
    for (var i=0; i<arrToFilter.length; i++) {

      let curReference = arrToFilter[i];
      const exist = initialArr.findIndex(initialRef => initialRef.refPhraseId == curReference.refPhraseId);
      if (exist != -1) {
        filteredArray.push(curReference.refPhraseId);
      }
    }
    return filteredArray;
  }
  checkPhraseOrderChanges(claimToolElement: ClaimToolElement, claimToolSaveChangesDataInitialValues: ClaimToolElementSaveChangesData) {
    var phrases = claimToolElement.phrasesOrder;
    for (let i=0; i<phrases.length; i++) {
      let curPhrase = phrases[i];
      if (claimToolSaveChangesDataInitialValues.phrasesOrder[i].phrase_id != curPhrase.phrase_id) {
        return true;
      }
    }
    return false;
  }

  
  private createSaveChangesPayload(claimToolElement: ClaimToolElement, claimToolSaveChangesDataInitialValues: ClaimToolElementSaveChangesData): ClaimToolSaveChangesModel {

    // create associated text array
    // create deleted associated text array
    let associatedPhrasesPayload:ClaimToolAssociatedTextPayload[] =[];
    let deletedPhrasesId:number[] = [];
    let associatedImagesToAddOrDeassociate:ClaimToolAssociatedImagePayload[] =[];
    let deletedAssocImagesIds:number[] = [];
    let referencesId: AddedReferencePhrasesPayload[] = [];
    let deletedReferencesId: number[] = [];


    associatedPhrasesPayload = this.filterAssocTextBasedOnInitial(claimToolElement.associatedText, claimToolSaveChangesDataInitialValues.associatedText, false);
    let deletedPhrases = this.filterAssocTextBasedOnInitial(claimToolElement.deletedAssociatedText, claimToolSaveChangesDataInitialValues.associatedText, true);
    deletedPhrasesId = deletedPhrases.map(phrase => phrase.text_id);

    associatedImagesToAddOrDeassociate = this.filterAssocImagesBasedOnInitial(claimToolElement.associatedImages, claimToolSaveChangesDataInitialValues.associatedImages, false);
    
    let deletedAssociatedImages = this.filterAssocImagesBasedOnInitial(claimToolElement.deletedAssociatedImages, claimToolSaveChangesDataInitialValues.associatedImages, true);
    deletedAssocImagesIds = deletedAssociatedImages.map(img => img.image_id);

    referencesId = this.filterReferencesBasedOnInitial(claimToolElement.references, claimToolSaveChangesDataInitialValues.references);
    deletedReferencesId = this.filterDeletedReferencesBasedOnInitial(claimToolElement.deletedReferences, claimToolSaveChangesDataInitialValues.references);

    const changedOrder = this.checkPhraseOrderChanges(claimToolElement, claimToolSaveChangesDataInitialValues);

    let claimToolSaveChanges = new ClaimToolSaveChangesModel();
    claimToolSaveChanges.associated_text = associatedPhrasesPayload;
    claimToolSaveChanges.deleted_associated_text = deletedPhrasesId;
    claimToolSaveChanges.associated_images = associatedImagesToAddOrDeassociate;
    claimToolSaveChanges.deleted_associated_images = deletedAssocImagesIds;
    claimToolSaveChanges.references = referencesId;
    claimToolSaveChanges.deleted_references = deletedReferencesId;
    claimToolSaveChanges.phrase_content_type = claimToolElement.contentType;
    claimToolSaveChanges.merged_phrases = claimToolElement.mergedPhrases.map(mp => mp.phrase_id);
    claimToolSaveChanges.split_phrases = claimToolElement.splitPhrases;
    claimToolSaveChanges.phrase_type_code = claimToolElement.phraseTypeCode;
    claimToolSaveChanges.phrase_sub_type_code = claimToolElement.phraseSubTypeCode;
    if (changedOrder) {
      claimToolSaveChanges.phrases_order = claimToolElement.phrasesOrder.map(phraseOrder => phraseOrder.phrase_id);
    } else {
      
      claimToolSaveChanges.phrases_order = [];
    }
    return claimToolSaveChanges;

    
  }
}